import { c as _c } from "react/compiler-runtime";
import { usePlausible } from "next-plausible";
import Link from "next/link";
import React, { useContext } from "react";
import { useDonationTotal, useWidgetSelector, useWidgetStore } from "../../../store/store";
import { registerDonation } from "../../../store/flows";
import { DonorContext } from "../../../../../../profile/layout/donorContext";
import { selectPaymentMethod, submitDonorInfo, setReferralCode, setDonorFormDraft } from "../../../store/actions";
import { State } from "../../../store/state";
import { DonorFormErrors, DonorFormField, DonorFormValues, donorFromForm, initialDonorFormValues, validateDonorForm } from "./donorForm";
import { PaymentMethod } from "../../../types/Enums";
import { WidgetPane2Props, WidgetProps } from "../../../types/WidgetProps";
import { ErrorField } from "../../shared/Error/ErrorField";
import { ToolTip } from "../../shared/ToolTip/ToolTip";
import { CustomCheckBox } from "./CustomCheckBox";
import { getEstimatedLtv } from "../../../../../../../util/ltv";
import { widgetTrpc } from "../../../store/widgetApi";
import AnimateHeight from "react-animate-height";
import { DonationSummary, DonationSummaryText } from "../../shared/DonationSummary/DonationSummary";
import { NextButton } from "../../shared/Buttons/NavigationButtons";
import { paymentMethodConfigurations } from "../../../config/methods";
import { useSsnValidation } from "./useSsnValidation";
import { RadioButtonGroup } from "../../../../RadioButton/RadioButtonGroup";
import { currencyForLocale } from "../../../utils/currency";
import panes from "../Panes.module.scss";
import forms from "../Forms.module.scss";
import styles from "./DonorPane.module.scss";
import navigationButtons from "../../shared/Buttons/NavigationButtons.module.scss";
import summary from "../../shared/DonationSummary/DonationSummary.module.scss";
import { Spinner } from "../../../../Spinner/Spinner";
export const DonorPane: React.FC<{
  locale: "en" | "no" | "sv" | "et" | "dk";
  text: WidgetPane2Props;
  summaryText: DonationSummaryText;
  paymentMethods: NonNullable<WidgetProps["methods"]>;
  isSingleCauseArea?: boolean;
}> = t0 => {
  const $ = _c(180);
  const {
    locale,
    text,
    summaryText,
    paymentMethods,
    isSingleCauseArea: t1
  } = t0;
  const isSingleCauseArea = t1 === undefined ? false : t1;
  const store = useWidgetStore();
  const {
    dispatch
  } = store;
  const donation = useWidgetSelector(_temp);
  const {
    donor: initialDonor
  } = useContext(DonorContext);
  const totalSumIncludingTip = useDonationTotal();
  let t2;
  if ($[0] !== donation || $[1] !== initialDonor) {
    t2 = () => initialDonorFormValues(donation, initialDonor);
    $[0] = donation;
    $[1] = initialDonor;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [values, setValues] = React.useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [errors, setErrors] = React.useState(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (field, value) => setValues(current => ({
      ...current,
      [field]: value
    }));
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const setValue = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = fields => setErrors(current_0 => {
      const next = {
        ...current_0
      };
      fields.forEach(field_0 => delete next[field_0]);
      return next;
    });
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const clearErrors = t5;
  const latestValues = React.useRef(values);
  let t6;
  let t7;
  if ($[6] !== values) {
    t6 = () => {
      latestValues.current = values;
    };
    t7 = [values];
    $[6] = values;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  React.useEffect(t6, t7);
  let t8;
  let t9;
  if ($[9] !== dispatch) {
    t8 = () => () => dispatch(setDonorFormDraft(latestValues.current));
    t9 = [dispatch];
    $[9] = dispatch;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  React.useEffect(t8, t9);
  const plausible = usePlausible();
  const {
    taxDeduction: taxDeductionChecked,
    newsletter: newsletterChecked,
    isAnonymous
  } = values;
  const [loadingMethod, setLoadingMethod] = React.useState(null);
  const submittingRef = React.useRef(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = React.useState(null);
  let t10;
  let t11;
  if ($[12] !== donation.apiError) {
    t10 = () => {
      if (typeof donation.apiError !== "undefined") {
        submittingRef.current = false;
        setLoadingMethod(null);
      }
    };
    t11 = [donation.apiError];
    $[12] = donation.apiError;
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  React.useEffect(t10, t11);
  let t12;
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      const onPageShow = e => {
        if (e.persisted) {
          submittingRef.current = false;
          setLoadingMethod(null);
        }
      };
      window.addEventListener("pageshow", onPageShow);
      return () => window.removeEventListener("pageshow", onPageShow);
    };
    t13 = [];
    $[15] = t12;
    $[16] = t13;
  } else {
    t12 = $[15];
    t13 = $[16];
  }
  React.useEffect(t12, t13);
  let t14;
  if ($[17] !== locale || $[18] !== taxDeductionChecked) {
    t14 = {
      locale,
      taxDeductionChecked
    };
    $[17] = locale;
    $[18] = taxDeductionChecked;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const {
    validateSsn,
    formatSsnInput,
    cprSuspicious
  } = useSsnValidation(t14);
  const [hasSyncedReferralCode, setHasSyncedReferralCode] = React.useState(donation.donorFormDraft !== undefined);
  if (donation.referralCode && !hasSyncedReferralCode) {
    setHasSyncedReferralCode(true);
    setValue("referralCode", donation.referralCode);
  }
  const mapPaymentMethod = _temp2;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[20] !== cprSuspicious || $[21] !== dispatch || $[22] !== donation.recurring || $[23] !== errors.email || $[24] !== errors.name || $[25] !== errors.privacyPolicy || $[26] !== errors.ssn || $[27] !== formatSsnInput || $[28] !== isAnonymous || $[29] !== isSingleCauseArea || $[30] !== loadingMethod || $[31] !== locale || $[32] !== newsletterChecked || $[33] !== paymentMethods || $[34] !== plausible || $[35] !== selectedPaymentMethodId || $[36] !== store || $[37] !== summaryText || $[38] !== taxDeductionChecked || $[39] !== text.allow_anonymous_donations || $[40] !== text.anon_button_text || $[41] !== text.anon_button_text_tooltip || $[42] !== text.email_invalid_error_text || $[43] !== text.email_placeholder || $[44] !== text.name_invalid_error_text || $[45] !== text.name_placeholder || $[46] !== text.newsletter_selector_text || $[47] !== text.pane2_button_text || $[48] !== text.privacy_policy_link || $[49] !== text.privacy_policy_required_error_text || $[50] !== text.privacy_policy_text || $[51] !== text.referral_code_placeholder || $[52] !== text.require_privacy_policy_checkbox || $[53] !== text.show_name_field || $[54] !== text.show_referral_code_field || $[55] !== text.tax_deduction_selector_text || $[56] !== text.tax_deduction_ssn_invalid_error_text || $[57] !== text.tax_deduction_ssn_placeholder || $[58] !== text.tax_deduction_ssn_suspicious_message || $[59] !== text.tax_deduction_tooltip_text || $[60] !== totalSumIncludingTip || $[61] !== validateSsn || $[62] !== values) {
    const handlePayment = methodId => {
      if (submittingRef.current) {
        return;
      }
      submittingRef.current = true;
      const validationErrors = validateDonorForm(values, {
        nameField: !!text.show_name_field,
        privacyPolicyCheckbox: !!text.privacy_policy_link && !!text.require_privacy_policy_checkbox,
        validSsn: validateSsn
      });
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) {
        submittingRef.current = false;
        return;
      }
      setLoadingMethod(methodId);
      const paymentMethod = mapPaymentMethod(methodId);
      const formData = values;
      if (!formData.isAnonymous) {
        plausible("SubmitDonorPane", {
          props: {
            donorType: formData.isAnonymous ? 0 : 1,
            taxDeduction: formData.taxDeduction,
            newsletter: formData.newsletter,
            method: paymentMethod
          }
        });
        if (donation.recurring) {
          if (paymentMethod === PaymentMethod.VIPPS) {
            plausible("SelectVippsRecurring");
          }
          if (paymentMethod === PaymentMethod.AVTALEGIRO) {
            plausible("SelectAvtaleGiro");
          }
          if (totalSumIncludingTip) {
            if (typeof window !== "undefined" && typeof window.fbq !== "undefined" && window.fbq != null) {
              getEstimatedLtv({
                method: paymentMethod,
                sum: totalSumIncludingTip,
                trpc: widgetTrpc()
              }).then(ltv => {
                window.fbq("track", "Lead", {
                  value: ltv,
                  currency: currencyForLocale(locale)
                });
              });
            }
          }
        }
        if (!donation.recurring) {
          if (paymentMethod === PaymentMethod.VIPPS) {
            plausible("SelectSingleVippsPayment");
          }
          if (paymentMethod === PaymentMethod.BANK) {
            plausible("SelectBankSingle");
          }
          if (typeof window !== "undefined") {
            if (window.fbq != null) {
              window.fbq("track", "Lead", {
                value: totalSumIncludingTip,
                currency: currencyForLocale(locale)
              });
            }
          }
        }
      }
      dispatch(submitDonorInfo(donorFromForm(formData, !!text.show_name_field)));
      if (text.show_referral_code_field) {
        dispatch(setReferralCode(formData.referralCode));
      }
      dispatch(selectPaymentMethod(paymentMethod));
      const configuration = paymentMethodConfigurations.find(config => config.id === methodId);
      registerDonation(store, {
        openExternalPaymentOnRegisterSuccess: configuration?.openExternalPaymentOnRegisterSuccess
      });
    };
    t22 = panes.pane;
    t20 = styles.donorForm;
    t21 = "on";
    t19 = panes.paneContainer;
    if ($[71] !== isSingleCauseArea || $[72] !== summaryText) {
      t15 = isSingleCauseArea ? <h2 className={panes.paneTitle}><wbr /></h2> : <DonationSummary text={summaryText} />;
      $[71] = isSingleCauseArea;
      $[72] = summaryText;
      $[73] = t15;
    } else {
      t15 = $[73];
    }
    if ($[74] !== isAnonymous || $[75] !== text.allow_anonymous_donations || $[76] !== text.anon_button_text || $[77] !== text.anon_button_text_tooltip) {
      t16 = text.allow_anonymous_donations && <div style={{
        marginBottom: "20px"
      }}><div className={forms.checkBoxWrapper} data-cy="anon-button-div"><input className={forms.hiddenCheckBox} data-cy="anon-checkbox" type="checkbox" onKeyDown={_temp3} name="isAnonymous" checked={isAnonymous} onChange={e_1 => {
            setValue("isAnonymous", e_1.target.checked);
            clearErrors(["name", "email", "ssn"]);
            (document.activeElement as HTMLElement).blur();
          }} /><CustomCheckBox label={text.anon_button_text} checked={isAnonymous} /><ToolTip text={text.anon_button_text_tooltip} /></div></div>;
      $[74] = isAnonymous;
      $[75] = text.allow_anonymous_donations;
      $[76] = text.anon_button_text;
      $[77] = text.anon_button_text_tooltip;
      $[78] = t16;
    } else {
      t16 = $[78];
    }
    const t23 = isAnonymous ? 0 : "auto";
    let t24;
    if ($[79] !== errors.name || $[80] !== text.name_invalid_error_text || $[81] !== text.name_placeholder || $[82] !== text.show_name_field || $[83] !== values.name) {
      t24 = text.show_name_field && <div className={forms.inputFieldWrapper}><input data-cy="name-input" type="text" placeholder={text.name_placeholder} name="name" value={values.name} onChange={e_2 => setValue("name", e_2.target.value)} />{errors.name && <ErrorField text={text.name_invalid_error_text} />}</div>;
      $[79] = errors.name;
      $[80] = text.name_invalid_error_text;
      $[81] = text.name_placeholder;
      $[82] = text.show_name_field;
      $[83] = values.name;
      $[84] = t24;
    } else {
      t24 = $[84];
    }
    let t25;
    if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = e_3 => setValue("email", e_3.target.value);
      $[85] = t25;
    } else {
      t25 = $[85];
    }
    let t26;
    if ($[86] !== text.email_placeholder || $[87] !== values.email) {
      t26 = <input data-cy="email-input" type="email" placeholder={text.email_placeholder} name="email" value={values.email} onChange={t25} />;
      $[86] = text.email_placeholder;
      $[87] = values.email;
      $[88] = t26;
    } else {
      t26 = $[88];
    }
    let t27;
    if ($[89] !== errors.email || $[90] !== text.email_invalid_error_text) {
      t27 = errors.email && <ErrorField text={text.email_invalid_error_text} />;
      $[89] = errors.email;
      $[90] = text.email_invalid_error_text;
      $[91] = t27;
    } else {
      t27 = $[91];
    }
    let t28;
    if ($[92] !== t26 || $[93] !== t27) {
      t28 = <div className={forms.inputFieldWrapper}>{t26}{t27}</div>;
      $[92] = t26;
      $[93] = t27;
      $[94] = t28;
    } else {
      t28 = $[94];
    }
    let t29;
    if ($[95] !== text.referral_code_placeholder || $[96] !== text.show_referral_code_field || $[97] !== values.referralCode) {
      t29 = text.show_referral_code_field && <div className={forms.inputFieldWrapper}><input data-cy="referral-code-input" type="text" autoComplete="off" placeholder={text.referral_code_placeholder} name="referralCode" value={values.referralCode} onChange={e_4 => setValue("referralCode", e_4.target.value)} /></div>;
      $[95] = text.referral_code_placeholder;
      $[96] = text.show_referral_code_field;
      $[97] = values.referralCode;
      $[98] = t29;
    } else {
      t29 = $[98];
    }
    let t30;
    if ($[99] !== taxDeductionChecked) {
      t30 = e_5 => {
        if (!taxDeductionChecked) {
          clearErrors(["ssn"]);
        }
        if (e_5.key === "Enter" || e_5.key === " ") {
          e_5.preventDefault();
        }
      };
      $[99] = taxDeductionChecked;
      $[100] = t30;
    } else {
      t30 = $[100];
    }
    let t31;
    if ($[101] !== taxDeductionChecked) {
      t31 = e_6 => {
        setValue("taxDeduction", e_6.target.checked);
        if (!taxDeductionChecked) {
          clearErrors(["ssn"]);
        }
        (document.activeElement as HTMLElement).blur();
      };
      $[101] = taxDeductionChecked;
      $[102] = t31;
    } else {
      t31 = $[102];
    }
    let t32;
    if ($[103] !== t30 || $[104] !== t31 || $[105] !== taxDeductionChecked) {
      t32 = <input className={forms.hiddenCheckBox} data-cy="tax-deduction-checkbox" type="checkbox" onKeyDown={t30} name="taxDeduction" checked={taxDeductionChecked} onChange={t31} />;
      $[103] = t30;
      $[104] = t31;
      $[105] = taxDeductionChecked;
      $[106] = t32;
    } else {
      t32 = $[106];
    }
    let t33;
    if ($[107] !== taxDeductionChecked || $[108] !== text.tax_deduction_selector_text) {
      t33 = <CustomCheckBox label={text.tax_deduction_selector_text} checked={taxDeductionChecked} />;
      $[107] = taxDeductionChecked;
      $[108] = text.tax_deduction_selector_text;
      $[109] = t33;
    } else {
      t33 = $[109];
    }
    let t34;
    if ($[110] !== taxDeductionChecked || $[111] !== text.tax_deduction_tooltip_text) {
      t34 = taxDeductionChecked && <ToolTip text={text.tax_deduction_tooltip_text} />;
      $[110] = taxDeductionChecked;
      $[111] = text.tax_deduction_tooltip_text;
      $[112] = t34;
    } else {
      t34 = $[112];
    }
    let t35;
    if ($[113] !== t32 || $[114] !== t33 || $[115] !== t34) {
      t35 = <div className={forms.checkBoxWrapper}>{t32}{t33}{t34}</div>;
      $[113] = t32;
      $[114] = t33;
      $[115] = t34;
      $[116] = t35;
    } else {
      t35 = $[116];
    }
    const t36 = taxDeductionChecked ? "auto" : 0;
    let t37;
    if ($[117] !== formatSsnInput) {
      t37 = e_7 => setValue("ssn", formatSsnInput(e_7.target.value));
      $[117] = formatSsnInput;
      $[118] = t37;
    } else {
      t37 = $[118];
    }
    let t38;
    if ($[119] !== t37 || $[120] !== text.tax_deduction_ssn_placeholder || $[121] !== values.ssn) {
      t38 = <input data-cy="ssn-input" type="text" inputMode="numeric" autoComplete="off" placeholder={text.tax_deduction_ssn_placeholder} name="ssn" value={values.ssn} onChange={t37} />;
      $[119] = t37;
      $[120] = text.tax_deduction_ssn_placeholder;
      $[121] = values.ssn;
      $[122] = t38;
    } else {
      t38 = $[122];
    }
    let t39;
    if ($[123] !== errors.ssn || $[124] !== text.tax_deduction_ssn_invalid_error_text) {
      t39 = errors.ssn && <ErrorField text={text.tax_deduction_ssn_invalid_error_text} />;
      $[123] = errors.ssn;
      $[124] = text.tax_deduction_ssn_invalid_error_text;
      $[125] = t39;
    } else {
      t39 = $[125];
    }
    let t40;
    if ($[126] !== cprSuspicious || $[127] !== text.tax_deduction_ssn_suspicious_message) {
      t40 = cprSuspicious && text.tax_deduction_ssn_suspicious_message && <div className={styles.infoMessageWrapper} data-cy="cpr-suspicious-message">{text.tax_deduction_ssn_suspicious_message}</div>;
      $[126] = cprSuspicious;
      $[127] = text.tax_deduction_ssn_suspicious_message;
      $[128] = t40;
    } else {
      t40 = $[128];
    }
    let t41;
    if ($[129] !== t38 || $[130] !== t39 || $[131] !== t40) {
      t41 = <div className={forms.inputFieldWrapper}>{t38}{t39}{t40}</div>;
      $[129] = t38;
      $[130] = t39;
      $[131] = t40;
      $[132] = t41;
    } else {
      t41 = $[132];
    }
    let t42;
    if ($[133] !== t36 || $[134] !== t41) {
      t42 = <AnimateHeight height={t36} animateOpacity={true} duration={200}>{t41}</AnimateHeight>;
      $[133] = t36;
      $[134] = t41;
      $[135] = t42;
    } else {
      t42 = $[135];
    }
    let t43;
    if ($[136] !== t35 || $[137] !== t42) {
      t43 = <div>{t35}{t42}</div>;
      $[136] = t35;
      $[137] = t42;
      $[138] = t43;
    } else {
      t43 = $[138];
    }
    let t44;
    if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
      t44 = e_9 => {
        setValue("newsletter", e_9.target.checked);
        (document.activeElement as HTMLElement).blur();
      };
      $[139] = t44;
    } else {
      t44 = $[139];
    }
    let t45;
    if ($[140] !== newsletterChecked) {
      t45 = <input className={forms.hiddenCheckBox} data-cy="newsletter-checkbox" type="checkbox" onKeyDown={_temp4} name="newsletter" checked={newsletterChecked} onChange={t44} />;
      $[140] = newsletterChecked;
      $[141] = t45;
    } else {
      t45 = $[141];
    }
    let t46;
    if ($[142] !== newsletterChecked || $[143] !== text.newsletter_selector_text) {
      t46 = <CustomCheckBox label={text.newsletter_selector_text} mobileLabel={text.newsletter_selector_text} checked={newsletterChecked} />;
      $[142] = newsletterChecked;
      $[143] = text.newsletter_selector_text;
      $[144] = t46;
    } else {
      t46 = $[144];
    }
    let t47;
    if ($[145] !== t45 || $[146] !== t46) {
      t47 = <div className={forms.checkBoxWrapper}>{t45}{t46}</div>;
      $[145] = t45;
      $[146] = t46;
      $[147] = t47;
    } else {
      t47 = $[147];
    }
    let t48;
    if ($[148] !== errors.privacyPolicy || $[149] !== text.privacy_policy_link || $[150] !== text.privacy_policy_required_error_text || $[151] !== text.privacy_policy_text || $[152] !== text.require_privacy_policy_checkbox || $[153] !== values.privacyPolicy) {
      t48 = text.privacy_policy_link && <div style={{
        marginTop: "10px"
      }}>{text.require_privacy_policy_checkbox && <><div style={{
            display: "flex",
            flexDirection: "row"
          }}><div className={forms.checkBoxWrapper}><input className={forms.hiddenCheckBox} data-cy="privacy-policy-checkbox" type="checkbox" onKeyDown={_temp5} name="privacyPolicy" checked={values.privacyPolicy} onChange={e_11 => {
                setValue("privacyPolicy", e_11.target.checked);
                (document.activeElement as HTMLElement).blur();
              }} /><CustomCheckBox label={text.privacy_policy_text} checked={values.privacyPolicy} /></div><Link href={`/${text.privacy_policy_link.slug}`} target="_blank" onClick={_temp6} style={{
              borderBottom: "1px solid var(--primary)",
              display: "inline-flex",
              height: "30px",
              marginTop: "5px",
              marginLeft: "7px"
            }}>{`${text.privacy_policy_link.title}  ↗`}</Link></div>{errors.privacyPolicy && <ErrorField text={text.privacy_policy_required_error_text} />}</>}{!text.require_privacy_policy_checkbox && <>{text.privacy_policy_text}{" "}<Link href={`/${text.privacy_policy_link.slug}`} target="_blank" onClick={_temp7} style={{
            borderBottom: "1px solid var(--primary)"
          }}>{`${text.privacy_policy_link.title}  ↗`}</Link></>}</div>;
      $[148] = errors.privacyPolicy;
      $[149] = text.privacy_policy_link;
      $[150] = text.privacy_policy_required_error_text;
      $[151] = text.privacy_policy_text;
      $[152] = text.require_privacy_policy_checkbox;
      $[153] = values.privacyPolicy;
      $[154] = t48;
    } else {
      t48 = $[154];
    }
    let t49;
    if ($[155] !== t43 || $[156] !== t47 || $[157] !== t48) {
      t49 = <div className={styles.checkBoxGroupWrapper}>{t43}{t47}{t48}</div>;
      $[155] = t43;
      $[156] = t47;
      $[157] = t48;
      $[158] = t49;
    } else {
      t49 = $[158];
    }
    if ($[159] !== t23 || $[160] !== t24 || $[161] !== t28 || $[162] !== t29 || $[163] !== t49) {
      t17 = <AnimateHeight height={t23} animateOpacity={true}>{t24}{t28}{t29}{t49}</AnimateHeight>;
      $[159] = t23;
      $[160] = t24;
      $[161] = t28;
      $[162] = t29;
      $[163] = t49;
      $[164] = t17;
    } else {
      t17 = $[164];
    }
    t18 = isSingleCauseArea ? <><RadioButtonGroup options={paymentMethods.map(_temp8)} selected={selectedPaymentMethodId ? paymentMethodMap[selectedPaymentMethodId] : undefined} onSelect={value_0 => {
        const method_1 = paymentMethods.find(m => paymentMethodMap[m._id] === value_0);
        if (method_1) {
          setSelectedPaymentMethodId(method_1._id);
        }
      }} /><div className={styles.actionBar} data-cy="next-button-div"><NextButton type="button" disabled={!selectedPaymentMethodId} onClick={e_14 => {
          e_14.preventDefault();
          if (selectedPaymentMethodId) {
            handlePayment(selectedPaymentMethodId);
          }
        }} data-cy="next-button">{loadingMethod ? <Spinner className={navigationButtons.spinner} /> : text.pane2_button_text}</NextButton></div></> : <div className={summary.paymentButtonsWrapper} style={{
      marginTop: "20px"
    }}>{paymentMethods.map(method_2 => <button className={summary.paymentButton} key={method_2._id} aria-busy={loadingMethod === method_2._id || undefined} onClick={e_15 => {
        e_15.preventDefault();
        handlePayment(method_2._id);
      }} data-cy={`payment-method-${method_2._id}`}>{loadingMethod === method_2._id ? <Spinner className={navigationButtons.spinner} /> : method_2.selector_text}</button>)}</div>;
    $[20] = cprSuspicious;
    $[21] = dispatch;
    $[22] = donation.recurring;
    $[23] = errors.email;
    $[24] = errors.name;
    $[25] = errors.privacyPolicy;
    $[26] = errors.ssn;
    $[27] = formatSsnInput;
    $[28] = isAnonymous;
    $[29] = isSingleCauseArea;
    $[30] = loadingMethod;
    $[31] = locale;
    $[32] = newsletterChecked;
    $[33] = paymentMethods;
    $[34] = plausible;
    $[35] = selectedPaymentMethodId;
    $[36] = store;
    $[37] = summaryText;
    $[38] = taxDeductionChecked;
    $[39] = text.allow_anonymous_donations;
    $[40] = text.anon_button_text;
    $[41] = text.anon_button_text_tooltip;
    $[42] = text.email_invalid_error_text;
    $[43] = text.email_placeholder;
    $[44] = text.name_invalid_error_text;
    $[45] = text.name_placeholder;
    $[46] = text.newsletter_selector_text;
    $[47] = text.pane2_button_text;
    $[48] = text.privacy_policy_link;
    $[49] = text.privacy_policy_required_error_text;
    $[50] = text.privacy_policy_text;
    $[51] = text.referral_code_placeholder;
    $[52] = text.require_privacy_policy_checkbox;
    $[53] = text.show_name_field;
    $[54] = text.show_referral_code_field;
    $[55] = text.tax_deduction_selector_text;
    $[56] = text.tax_deduction_ssn_invalid_error_text;
    $[57] = text.tax_deduction_ssn_placeholder;
    $[58] = text.tax_deduction_ssn_suspicious_message;
    $[59] = text.tax_deduction_tooltip_text;
    $[60] = totalSumIncludingTip;
    $[61] = validateSsn;
    $[62] = values;
    $[63] = t15;
    $[64] = t16;
    $[65] = t17;
    $[66] = t18;
    $[67] = t19;
    $[68] = t20;
    $[69] = t21;
    $[70] = t22;
  } else {
    t15 = $[63];
    t16 = $[64];
    t17 = $[65];
    t18 = $[66];
    t19 = $[67];
    t20 = $[68];
    t21 = $[69];
    t22 = $[70];
  }
  let t23;
  if ($[165] !== t15 || $[166] !== t16 || $[167] !== t17 || $[168] !== t18) {
    t23 = <div>{t15}{t16}{t17}{t18}</div>;
    $[165] = t15;
    $[166] = t16;
    $[167] = t17;
    $[168] = t18;
    $[169] = t23;
  } else {
    t23 = $[169];
  }
  let t24;
  if ($[170] !== t19 || $[171] !== t23) {
    t24 = <div className={t19}>{t23}</div>;
    $[170] = t19;
    $[171] = t23;
    $[172] = t24;
  } else {
    t24 = $[172];
  }
  let t25;
  if ($[173] !== t20 || $[174] !== t21 || $[175] !== t24) {
    t25 = <form className={t20} autoComplete={t21}>{t24}</form>;
    $[173] = t20;
    $[174] = t21;
    $[175] = t24;
    $[176] = t25;
  } else {
    t25 = $[176];
  }
  let t26;
  if ($[177] !== t22 || $[178] !== t25) {
    t26 = <div className={t22}>{t25}</div>;
    $[177] = t22;
    $[178] = t25;
    $[179] = t26;
  } else {
    t26 = $[179];
  }
  return t26;
};
export const paymentMethodMap: Record<string, PaymentMethod> = {
  vipps: PaymentMethod.VIPPS,
  bank: PaymentMethod.BANK,
  avtalegiro: PaymentMethod.AVTALEGIRO,
  quickpay_card: PaymentMethod.QUICKPAY_CARD,
  quickpay_mobilepay: PaymentMethod.QUICKPACK_MOBILEPAY,
  dkbank: PaymentMethod.DKBANK
};
function _temp(state) {
  return state.donation;
}
function _temp2(method) {
  const mapped = paymentMethodMap[method];
  if (!mapped) {
    throw new Error(`Unknown payment method: ${method}`);
  }
  return mapped;
}
function _temp3(e_0) {
  if (e_0.key === "Enter" || e_0.key === " ") {
    e_0.preventDefault();
  }
}
function _temp4(e_8) {
  if (e_8.key === "Enter" || e_8.key === " ") {
    e_8.preventDefault();
  }
}
function _temp5(e_10) {
  if (e_10.key === "Enter" || e_10.key === " ") {
    e_10.preventDefault();
  }
}
function _temp6(e_12) {
  e_12.currentTarget.blur();
}
function _temp7(e_13) {
  e_13.currentTarget.blur();
}
function _temp8(method_0) {
  return {
    title: method_0.selector_text,
    value: paymentMethodMap[method_0._id],
    data_cy: `payment-method-${method_0._id}`
  };
}